/*
 * This file is part of the Det Norske Teatret application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */
import React, { forwardRef } from 'react';
import { Link } from 'react-router';
import classnames from 'classnames';

const DEFAULT_ITEMS: MenuItem[] = [];
const DEFAULT_ON_CLOSE = () => {};
const DEFAULT_ON_CLICK = () => {};

interface MenuItem {
  id: string | number;
  url?: string;
  text: string;
  active?: boolean;
}

interface MenuProps {
  id?: string;
  controller?: string;
  active?: boolean;
  items?: MenuItem[];
  onClick?: (id: string | number) => void;
  onClose?: () => void;
}

/**
 * This is the Menu component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 */

const Menu = forwardRef<HTMLElement, MenuProps>(
  (
    {
      id,
      controller = 'button',
      items = DEFAULT_ITEMS,
      active = false,
      onClick = DEFAULT_ON_CLICK,
      onClose = DEFAULT_ON_CLOSE,
    },
    ref
  ) => (
    <nav
      ref={ref}
      id={id}
      className="menu"
      role="menu"
      aria-labelledby={controller}
    >
      <div className="items">
        {items.map(({ id, url, text, active: activeLink }) => {
          if (url) {
            return (
              <Link
                key={id}
                to={url}
                className={classnames({ active: activeLink })}
                role="menuitem"
                tabIndex={active ? 0 : -1}
                dangerouslySetInnerHTML={{ __html: text }}
                onClick={() => {
                  onClick(id);
                  onClose();
                }}
              />
            );
          }

          return <span key={id} dangerouslySetInnerHTML={{ __html: text }} />;
        })}
      </div>
    </nav>
  )
);

export default Menu;
